import { type MessageKey, onLanguageChange, t } from '../i18n';

/**
 * The drawer behind the hamburger button.
 *
 * It exists because the header ran out of room: the language picker, the
 * responsible-gambling card, the nickname and sign-out all used to sit in one
 * row with the balance and the history strip, and on a 360px phone the last of
 * them fell off the edge.
 *
 * Two levels only: a root list, and one sub-page at a time. A drawer that can
 * nest indefinitely needs a real navigation stack and a back gesture; every
 * screen this game has fits in one step, so `showPage` replaces rather than
 * pushes.
 *
 * A sub-page is built by a callback rather than by markup because its contents
 * depend on session data -- limits, commitments, the chosen avatar. The callback
 * is kept so a language change can re-run it: text built from script has to
 * redraw itself, which is the same rule hud.ts and feed.ts follow.
 */
export class Menu {
  private readonly root = must('menu');
  private readonly panel = must('menu-panel');
  private readonly backdrop = must('menu-backdrop');
  private readonly head = {
    back: mustButton('menu-back'),
    close: mustButton('menu-close'),
    avatar: must('menu-avatar'),
    nickname: must('nickname'),
    title: must('menu-title'),
  };
  private readonly rootList = must('menu-root');
  private readonly pageHost = must('menu-page');
  private readonly opener = mustButton('btn-menu');

  /** The open sub-page, kept so a language change can rebuild it. */
  private page: { titleKey: MessageKey; build: (host: HTMLElement) => void } | null = null;

  constructor() {
    this.opener.addEventListener('click', () => this.toggle());
    this.head.close.addEventListener('click', () => this.close());
    this.backdrop.addEventListener('click', () => this.close());
    this.head.back.addEventListener('click', () => this.showRoot());

    // Escape backs out one level, which is what the same key does in every
    // dialog a player has ever used: first the page, then the drawer.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.isOpen) return;
      e.preventDefault();
      if (this.page) this.showRoot();
      else this.close();
    });

    onLanguageChange(() => {
      if (this.page) this.renderPage();
    });
  }

  get isOpen(): boolean {
    return !this.root.hasAttribute('hidden');
  }

  open(): void {
    this.root.removeAttribute('hidden');
    // The close button, not the first item: opening a menu should not put the
    // keyboard one Enter away from signing out.
    this.head.close.focus();
  }

  close(): void {
    this.root.setAttribute('hidden', '');
    this.showRoot();
    this.opener.focus();
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  /**
   * Adds an item to the root list.
   *
   * `before` names an existing element id to insert ahead of, which is how the
   * order in the specification survives being assembled from several modules:
   * each one says where it belongs rather than relying on load order.
   */
  addItem(opts: {
    id: string;
    labelKey: MessageKey;
    onClick: () => void;
    before?: string;
    danger?: boolean;
  }): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = opts.id;
    button.className = opts.danger ? 'menu-item danger' : 'menu-item';

    const label = document.createElement('span');
    label.dataset['i18n'] = opts.labelKey;
    label.textContent = t(opts.labelKey);

    const chevron = document.createElement('span');
    chevron.className = 'chevron';
    chevron.textContent = '›';

    button.append(label, chevron);
    button.addEventListener('click', opts.onClick);

    const anchor = opts.before ? document.getElementById(opts.before) : null;
    if (anchor && anchor.parentElement === this.rootList) this.rootList.insertBefore(button, anchor);
    else this.rootList.appendChild(button);

    return button;
  }

  /** Adds a row that holds its own control (a switch, a select) rather than navigating. */
  addRow(opts: { labelKey: MessageKey; control: HTMLElement; before?: string }): HTMLElement {
    const row = document.createElement('div');
    row.className = 'menu-row';

    const label = document.createElement('span');
    label.dataset['i18n'] = opts.labelKey;
    label.textContent = t(opts.labelKey);

    row.append(label, opts.control);

    const anchor = opts.before ? document.getElementById(opts.before) : null;
    if (anchor && anchor.parentElement === this.rootList) this.rootList.insertBefore(row, anchor);
    else this.rootList.appendChild(row);

    return row;
  }

  /**
   * Adds an explanatory line to the root list.
   *
   * Used by the audio switches: there is no sound in the game yet, and a switch
   * that does nothing with no word about why reads as broken.
   */
  addNote(textKey: MessageKey, before?: string): HTMLElement {
    const note = document.createElement('p');
    note.className = 'menu-note';
    note.dataset['i18n'] = textKey;
    note.textContent = t(textKey);

    const anchor = before ? document.getElementById(before) : null;
    if (anchor && anchor.parentElement === this.rootList) this.rootList.insertBefore(note, anchor);
    else this.rootList.appendChild(note);

    return note;
  }

  /** Opens the drawer on a sub-page, building it from the callback. */
  showPage(titleKey: MessageKey, build: (host: HTMLElement) => void): void {
    this.page = { titleKey, build };
    this.renderPage();
    if (!this.isOpen) this.open();
  }

  private renderPage(): void {
    if (!this.page) return;

    this.head.title.textContent = t(this.page.titleKey);
    this.head.title.removeAttribute('hidden');
    this.head.back.removeAttribute('hidden');
    this.head.avatar.setAttribute('hidden', '');
    this.head.nickname.setAttribute('hidden', '');

    this.rootList.setAttribute('hidden', '');
    this.pageHost.removeAttribute('hidden');
    this.pageHost.className = 'menu-page';
    this.page.build(this.pageHost);
  }

  private showRoot(): void {
    this.page = null;
    this.pageHost.replaceChildren();
    this.pageHost.setAttribute('hidden', '');
    this.rootList.removeAttribute('hidden');

    this.head.title.setAttribute('hidden', '');
    this.head.back.setAttribute('hidden', '');
    this.head.avatar.removeAttribute('hidden');
    this.head.nickname.removeAttribute('hidden');
  }

  /** The drawer's own panel, for a caller that needs to measure or scroll it. */
  get panelElement(): HTMLElement {
    return this.panel;
  }
}

function must(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`menu: #${id} is missing from the page`);
  return el;
}

function mustButton(id: string): HTMLButtonElement {
  const el = document.getElementById(id);
  if (!(el instanceof HTMLButtonElement)) throw new Error(`menu: #${id} is not a button`);
  return el;
}
